import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ExpressCheckout from '../components/ExpressCheckout';
import PostcodeChecker from '../components/PostcodeChecker';
import { fallbackProducts } from '../prerenderData';
import { apiFetch } from '../services/api';
import { fmtPrice, formatSaleEnds } from '../utils/pricing';
import { findColour, swatchStyle, STOCK_COLOUR_CODE } from '../data/sauledaColours';
import { MADE_TO_ORDER_BANDS } from '../config/leadTimes';
import { BRACKET_COUNT } from '../config/installation';
import useAwningConfig from '../hooks/useAwningConfig';
import { CheckIcon } from '../components/icons';
import ProjectVideo from '../components/home/ProjectVideo';
import AccessoryPicker from '../components/product/AccessoryPicker';
import { getProductContent } from '../data/productContent';
import './ConfiguratorPage.css';

// Full-page awning configurator: /configure/:slug
//
// The product page's compact widget covers "what does this cost"; this page
// is for sitting down and building the awning - a large preview that follows
// the fabric choice, every size with its price, every colour, the delivery
// check and the buy button, on one screen. It is the page the mobile sticky
// "Customise" bar opens. The rules (sizes, prices, stock, lead time, checkout
// URL) come from useAwningConfig, shared with the widget.
//
// URL parameters `w`, `p` and `colour_code` preselect a configuration, so a
// link can land on, say, the 4 x 3 m in charcoal. They are applied after
// mount rather than in the first render: the pre-rendered HTML has nothing
// selected, and the first client render must match it for hydration.

function SwatchButton({ variant, selected, onClick, size = 'md', mostPopular = false }) {
  const def = findColour(variant.code);
  const isStripe = def ? !!def.stripes : false;
  const cls = [
    'cfg-swatch',
    `cfg-swatch--${size}`,
    selected ? 'cfg-swatch--selected' : '',
    isStripe ? 'cfg-swatch--stripe' : '',
    mostPopular ? 'cfg-swatch--most-popular' : '',
  ].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${variant.name} (${variant.code})`}
      title={variant.name}
    >
      <span className="cfg-swatch-chip" style={def ? swatchStyle(def, { repeatPx: size === 'lg' ? 40 : 26 }) : { backgroundColor: variant.swatch || '#ccc' }} />
      {size === 'lg' && <span className="cfg-swatch-name">{variant.name}</span>}
    </button>
  );
}

export default function ConfiguratorPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();

  // Seed from the build snapshot so the pre-rendered page and the first
  // client render match; the live record replaces it after mount.
  const snapshot = fallbackProducts.find((p) => p.slug === slug) || null;
  const [product, setProduct] = useState(snapshot);
  const [loading, setLoading] = useState(!snapshot);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/products/${encodeURIComponent(slug)}`)
      .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then((p) => { if (!cancelled) { setProduct(p); setLoading(false); } })
      .catch(() => { if (!cancelled) { if (!snapshot) setNotFound(true); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const cfg = useAwningConfig(product);
  const { selectSize, setSelectedColour, sizes, variants } = cfg;
  useEffect(() => {
    const w = params.get('w');
    const p = params.get('p');
    const code = params.get('colour_code');
    if (w && p && sizes.some((s) => String(s.width) === w && String(s.projection) === p)) selectSize(w, p);
    if (code) {
      const i = variants.findIndex((v) => v.code === code);
      if (i !== -1) setSelectedColour(i);
    }
    // Only on first load and when the product record arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizes.length, variants.length]);

  const [colourTab, setColourTab] = useState('solid');
  const [postcodeResult, setPostcodeResult] = useState(null);

  // Preview: the chosen colour's own photo when there is one, otherwise the
  // product's lead image. Thumbnails let the visitor flick through the
  // product shots without losing the configuration.
  const gallery = useMemo(() => (product?.images || []).filter(Boolean).slice(0, 6), [product]);
  const colourImage = cfg.colour?.image || null;
  const [previewIdx, setPreviewIdx] = useState(0);
  const previewSrc = colourImage || gallery[previewIdx] || gallery[0] || null;

  if (notFound) {
    return (
      <div className="cfg-page">
        <SEO title="Configure your awning" description="Build your ASG Shading awning - choose a size, pick a fabric colour and see the price." noindex />
        <div className="cfg-empty">
          <h1>We could not find that awning.</h1>
          <p><Link to="/products/awnings">See the awning range</Link></p>
        </div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="cfg-page">
        <SEO title="Configure your awning" description="Build your ASG Shading awning - choose a size, pick a fabric colour and see the price." noindex />
        <div className="cfg-empty"><h1>Configure your awning</h1><p>{loading ? 'Loading…' : ''}</p></div>
      </div>
    );
  }

  const configurable = product.category === 'awning' && cfg.sizes.length > 0;
  const shortName = product.name.split(' ')[0];
  const hasAddons = cfg.accessories.length > 0;
  // Steps are numbered in render order; the accessories step only exists
  // when the product offers add-ons.
  const stepNum = (() => { let n = 0; return () => ++n; })();
  const installVideo = getProductContent(product.slug)?.installation?.video || null;
  const fromPrice = cfg.sizes.length ? Math.min(...cfg.sizes.filter((s) => s.price != null).map((s) => s.price)) : null;
  const headlinePrice = cfg.price != null ? cfg.price : fromPrice;
  const summaryParts = [
    cfg.customMode && cfg.dimsReady ? `${cfg.displayWidth} × ${cfg.displayProjection} m (custom)` : (cfg.selectedSize ? `${cfg.selectedSize.width} × ${cfg.selectedSize.projection} m` : null),
    cfg.colourName || null,
    // Paid add-ons only - the included ones are not a choice the visitor made.
    (() => {
      const n = cfg.addons.lines.filter((l) => !l.included).reduce((sum, l) => sum + l.quantity, 0);
      return n ? `+ ${n} accessor${n === 1 ? 'y' : 'ies'}` : null;
    })(),
  ].filter(Boolean);

  const quoteUrl = (() => {
    const q = new URLSearchParams();
    q.set('product', product.slug);
    if (cfg.displayWidth) q.set('w', cfg.displayWidth);
    if (cfg.displayProjection) q.set('p', cfg.displayProjection);
    if (cfg.colourCode) q.set('colour', cfg.colourCode);
    return `/request-quote?${q.toString()}`;
  })();

  return (
    <div className="cfg-page">
      <SEO
        title={`Configure the ${product.name}`}
        description={`Build your ${product.name}: choose a size, pick one of ${cfg.variants.length} Sauleda fabric colours and see the price, delivery and lead time before you order.`}
        canonical={`/products/${product.slug}`}
        noindex
      />
      <Breadcrumbs items={[{ name: 'Products', url: '/products' }, { name: product.name, url: `/products/${product.slug}` }, { name: 'Configure' }]} />

      <div className="cfg-layout">
        {/* -- Preview ----------------------------------------------------- */}
        <div className="cfg-preview">
          <div className="cfg-preview-frame">
            {previewSrc && <img src={previewSrc} alt={cfg.colourName ? `${product.name} in ${cfg.colourName}` : product.name} width={1200} height={900} />}
            {cfg.colourName && (
              <span className="cfg-preview-tag">
                <span className="cfg-preview-tag-chip" style={cfg.colour ? swatchStyle(findColour(cfg.colour.code) || cfg.colour, { repeatPx: 16 }) : undefined} />
                {cfg.colourName}{colourImage ? '' : ' - photo shows the stock colour'}
              </span>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="cfg-thumbs" role="list">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  role="listitem"
                  className={`cfg-thumb${!colourImage && i === previewIdx ? ' cfg-thumb--active' : ''}`}
                  onClick={() => { setPreviewIdx(i); if (colourImage) cfg.setSelectedColour(null); }}
                  aria-label={`${product.name} photo ${i + 1}`}
                >
                  <img src={src} alt="" loading="lazy" width={160} height={120} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* -- Panel ------------------------------------------------------- */}
        <div className="cfg-panel">
          <p className="cfg-eyebrow">Configure</p>
          <h1 className="cfg-title">{product.name}</h1>

          <div className="cfg-pricebar">
            {headlinePrice != null ? (
              <>
                <span className="cfg-price">
                  {cfg.price == null && <span className="cfg-price-from">from </span>}
                  ${fmtPrice(headlinePrice)}
                </span>
                {cfg.sale.active && cfg.sale.rrp != null && cfg.price != null && cfg.sale.rrp > cfg.price && (
                  <span className="cfg-price-was">${fmtPrice(cfg.sale.rrp)}</span>
                )}
                <span className="cfg-price-note">incl. GST</span>
              </>
            ) : (
              <span className="cfg-price">Price on request</span>
            )}
          </div>
          {cfg.sale.active && cfg.sale.end && (
            <p className="cfg-sale-ends">{cfg.sale.label ? `${cfg.sale.label} - ` : ''}{formatSaleEnds(cfg.sale.end)}</p>
          )}
          <p className="cfg-lead">
            <span className={`cfg-lead-dot${cfg.isInStock ? ' cfg-lead-dot--stock' : ''}`} aria-hidden="true" />
            {cfg.variantReady
              ? (cfg.isInStock ? 'In stock - ships next business day' : `Made to order - ${cfg.leadTime}`)
              : `Made to order: ${MADE_TO_ORDER_BANDS}. In-stock sizes ship next business day.`}
          </p>

          {!configurable ? (
            <div className="cfg-step">
              <p>This product is quoted rather than configured online.</p>
              <Link to="/request-quote" className="cfg-buy">Request a quote</Link>
            </div>
          ) : (
            <>
              {/* Step 1: size */}
              <section className="cfg-step" aria-labelledby="cfg-size-h">
                <h2 id="cfg-size-h" className="cfg-step-title">
                  <span className="cfg-step-num">{stepNum()}</span> Size <span className="cfg-step-sub">Width × projection</span>
                </h2>
                {!cfg.customMode ? (
                  <>
                    <div className="cfg-chips" role="radiogroup" aria-label="Awning size">
                      {cfg.sizes.map((s) => {
                        const selected = cfg.selectedSize === s;
                        const stock = cfg.stockWidths.has(Number(s.width));
                        return (
                          <button
                            key={`${s.width}x${s.projection}`}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`cfg-chip${selected ? ' cfg-chip--selected' : ''}`}
                            onClick={() => cfg.selectSize(s.width, s.projection)}
                          >
                            <span className="cfg-chip-size">{s.width} × {s.projection} m</span>
                            {s.price != null && <span className="cfg-chip-price">${fmtPrice(s.price)}</span>}
                            {stock && <span className="cfg-chip-stock" title="In stock in one or more colours" aria-label="in stock" />}
                          </button>
                        );
                      })}
                    </div>
                    <button type="button" className="cfg-textlink" onClick={() => cfg.setCustomMode(true)}>
                      Need a size that is not listed?
                    </button>
                  </>
                ) : (
                  <>
                    <div className="cfg-custom">
                      <label className="cfg-field">
                        <span>Width (m)</span>
                        <input type="text" inputMode="decimal" placeholder="e.g. 4.2" value={cfg.customWidth} onChange={(e) => cfg.setCustomWidth(e.target.value)} />
                      </label>
                      <span className="cfg-custom-x" aria-hidden="true">×</span>
                      <label className="cfg-field">
                        <span>Projection (m)</span>
                        <input type="text" inputMode="decimal" placeholder="e.g. 2.8" value={cfg.customProjection} onChange={(e) => cfg.setCustomProjection(e.target.value)} />
                      </label>
                    </div>
                    <p className="cfg-help">Custom sizes are priced by quote and made to order: {MADE_TO_ORDER_BANDS}.</p>
                    <button type="button" className="cfg-textlink" onClick={() => cfg.setCustomMode(false)}>Back to standard sizes</button>
                  </>
                )}
              </section>

              {/* Step 2: colour */}
              <section className="cfg-step" aria-labelledby="cfg-colour-h">
                <h2 id="cfg-colour-h" className="cfg-step-title">
                  <span className="cfg-step-num">{stepNum()}</span> Fabric colour
                  <span className="cfg-step-sub">
                    {cfg.colourName ? <>{cfg.colourName} <span className="cfg-code">{cfg.colourCode}</span></> : `${cfg.variants.length} Sauleda colourways`}
                  </span>
                </h2>
                {cfg.popular.length > 0 && (
                  <div className="cfg-popular">
                    <p className="cfg-group-label">Popular</p>
                    <div className="cfg-swatches cfg-swatches--lg">
                      {cfg.popular.map((v) => (
                        <SwatchButton
                          key={v.code}
                          variant={v}
                          size="lg"
                          selected={cfg.colour?.code === v.code}
                          mostPopular={v.code === cfg.mostPopularCode}
                          onClick={() => cfg.toggleColour(cfg.variants.indexOf(v))}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className="cfg-tabs" role="tablist" aria-label="Colour groups">
                  <button type="button" role="tab" aria-selected={colourTab === 'solid'} className={`cfg-tab${colourTab === 'solid' ? ' cfg-tab--active' : ''}`} onClick={() => setColourTab('solid')}>
                    Solids <span className="cfg-tab-count">{cfg.solids.length + cfg.popular.filter((v) => !v.code?.startsWith('AW-ST')).length}</span>
                  </button>
                  <button type="button" role="tab" aria-selected={colourTab === 'stripe'} className={`cfg-tab${colourTab === 'stripe' ? ' cfg-tab--active' : ''}`} onClick={() => setColourTab('stripe')}>
                    Stripes <span className="cfg-tab-count">{cfg.stripes.length + cfg.popular.filter((v) => v.code?.startsWith('AW-ST')).length}</span>
                  </button>
                </div>
                <div className="cfg-swatches" role="tabpanel">
                  {(colourTab === 'solid'
                    ? cfg.variants.filter((v) => !v.code?.startsWith('AW-ST'))
                    : cfg.variants.filter((v) => v.code?.startsWith('AW-ST'))
                  ).map((v) => (
                    <SwatchButton
                      key={v.code}
                      variant={v}
                      selected={cfg.colour?.code === v.code}
                      onClick={() => cfg.toggleColour(cfg.variants.indexOf(v))}
                    />
                  ))}
                </div>
                <p className="cfg-help">
                  <span className="cfg-stock-key" aria-hidden="true" /> {findColour(STOCK_COLOUR_CODE)?.name || 'Charcoal grey'} is held in stock. Screens vary - <Link to="/colours">see the colours page</Link> for the full chart.
                </p>
              </section>

              {/* Step 3: optional accessories. Included ones show as already
                  on the order; paid ones take a quantity. */}
              {hasAddons && (
                <section className="cfg-step" aria-labelledby="cfg-addons-h">
                  <h2 id="cfg-addons-h" className="cfg-step-title">
                    <span className="cfg-step-num">{stepNum()}</span> Optional accessories
                    {cfg.addons.total > 0 && <span className="cfg-step-sub">+${fmtPrice(cfg.addons.total)}</span>}
                  </h2>
                  <AccessoryPicker
                    options={cfg.accessories}
                    quantities={cfg.addonQty}
                    onChange={cfg.setAddonQty}
                    productName={shortName}
                  />
                </section>
              )}

              {/* What's included */}
              {product.features?.length > 0 && (
                <section className="cfg-step" aria-labelledby="cfg-incl-h">
                  <h2 id="cfg-incl-h" className="cfg-step-title"><span className="cfg-step-num">{stepNum()}</span> Included with every {shortName}</h2>
                  <ul className="cfg-included">
                    {product.features.slice(0, 6).map((f) => (
                      <li key={f.title}><CheckIcon /><span><strong>{f.title}</strong>{f.description ? ` - ${f.description}` : ''}</span></li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Delivery */}
              <section className="cfg-step" aria-labelledby="cfg-del-h">
                <h2 id="cfg-del-h" className="cfg-step-title"><span className="cfg-step-num">{stepNum()}</span> Delivery</h2>
                <PostcodeChecker compact onResult={setPostcodeResult} />
                {!postcodeResult && <p className="cfg-help">Free to most of Melbourne, $150 flat to the outer suburbs, freight quoted elsewhere.</p>}
              </section>

              {/* Installation - DIY in about an hour, plug-in power */}
              <section className="cfg-step" aria-labelledby="cfg-install-h">
                <h2 id="cfg-install-h" className="cfg-step-title"><span className="cfg-step-num">{stepNum()}</span> Installation <span className="cfg-step-sub">About an hour, DIY</span></h2>
                {installVideo && (
                  <div className="cfg-video">
                    <ProjectVideo video={installVideo} title={installVideo.title} mode="guide" />
                  </div>
                )}
                <ul className="cfg-included">
                  <li><CheckIcon /><span><strong>About an hour with a helper</strong> - {BRACKET_COUNT} on the wall, lift the cassette on, clip it in. Fixings are supplied.</span></li>
                  <li><CheckIcon /><span><strong>No electrician needed</strong> - the motor comes with a standard plug. Plug into a nearby power point and it is ready. Only if there is no power point within reach would you need one put in.</span></li>
                  <li><CheckIcon /><span><strong>Prefer not to?</strong> Any handyman can do it. The manual, wind report and measuring guide are free to download.</span></li>
                </ul>
                <p className="cfg-help"><Link to="/measuring-guide">Measuring guide</Link> · <Link to="/for-installers">Installation information</Link></p>
              </section>

              {/* Summary + buy. Also mirrored in the sticky bar on phones. */}
              <div className="cfg-summary" id="cfg-summary">
                <div className="cfg-summary-row">
                  <div>
                    <p className="cfg-summary-title">Your {shortName}</p>
                    <p className="cfg-summary-detail">{summaryParts.length ? summaryParts.join(' · ') : 'Choose a size and a colour'}</p>
                  </div>
                  <p className="cfg-summary-price">{cfg.total != null ? `$${fmtPrice(cfg.total)}` : (cfg.customMode ? 'Quote' : '')}</p>
                </div>
                {cfg.addons.lines.length > 0 && (
                  <ul className="cfg-summary-addons">
                    {cfg.addons.lines.map((l) => (
                      <li key={l.slug}>
                        <span>{l.quantity > 1 ? `${l.quantity} × ` : ''}{l.name}</span>
                        <span>{l.included ? 'Included' : `$${fmtPrice(l.lineTotal)}`}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {cfg.checkoutUrl ? (
                  <>
                    <a href={cfg.checkoutUrl} className="cfg-buy" rel="nofollow">Buy now</a>
                    <ExpressCheckout
                      product={product}
                      width={cfg.selectedWidth}
                      projection={cfg.selectedProjection}
                      colourName={cfg.colourName}
                      colourCode={cfg.colourCode}
                      addons={cfg.addons.lines}
                      addonQty={cfg.addonQty}
                      amountCents={Math.round(cfg.total * 100)}
                    />
                    <Link to={quoteUrl} className="cfg-textlink cfg-textlink--center">Prefer a written quote first?</Link>
                  </>
                ) : cfg.customMode && cfg.dimsReady ? (
                  <Link to={quoteUrl} className="cfg-buy">Request a quote for this size</Link>
                ) : (
                  <>
                    <button type="button" className="cfg-buy" disabled>
                      {!cfg.dimsReady ? 'Choose a size' : 'Choose a colour'}
                    </button>
                    <Link to={quoteUrl} className="cfg-textlink cfg-textlink--center">Not sure? Request a quote</Link>
                  </>
                )}
                <p className="cfg-summary-note">Pay in full at checkout, or 50% deposit on large orders by quote. Supply only - fit it yourself in about an hour, or any handyman can.</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Phone: sticky price + action, mirrors the summary card. */}
      {configurable && (
        <div className="cfg-sticky" role="region" aria-label="Your configuration">
          <div className="cfg-sticky-text">
            <span className="cfg-sticky-price">{cfg.total != null ? `$${fmtPrice(cfg.total)}` : (headlinePrice != null ? `from $${fmtPrice(headlinePrice)}` : 'Price on request')}</span>
            <span className="cfg-sticky-detail">{summaryParts.length ? summaryParts.join(' · ') : 'Choose a size and colour'}</span>
          </div>
          {cfg.checkoutUrl ? (
            <a href={cfg.checkoutUrl} className="cfg-buy cfg-buy--sticky" rel="nofollow">Buy now</a>
          ) : cfg.customMode && cfg.dimsReady ? (
            <Link to={quoteUrl} className="cfg-buy cfg-buy--sticky">Get a quote</Link>
          ) : (
            <a href="#cfg-size-h" className="cfg-buy cfg-buy--sticky cfg-buy--ghost">{!cfg.dimsReady ? 'Choose size' : 'Choose colour'}</a>
          )}
        </div>
      )}
    </div>
  );
}
